import { json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/cloudflare';
import { useLoaderData } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';
import { ProjectImport } from '~/components/chat/Chat.client';
import { IndexLayout, type IndexLoaderData } from '~/layouts/IndexLayout';

import { handleWithAuth } from '~/lib/.server/auth-middleware';
import { getAssociatedGithubRepository, isLinkedToGithub } from '~/lib/.server/github';
import { getTokenStats } from '~/lib/.server/rateLimit';
import { withUserData } from '~/lib/.server/user';
import { loadProjectInfo } from '~/lib/.server/utils/project-info';
import { SOCIAL_PREVIEW_ORIGIN, DEFAULT_PROJECT_INFO, GITHUB_BRANCH_PARAM, type ProjectInfo } from '~/lib/constants';
import { getMetaTags } from '~/utils/meta';

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const { projectInfo, slug } = data ?? {};
  const description = projectInfo?.description ?? DEFAULT_PROJECT_INFO.description;

  return getMetaTags({
    user: data?.user,
    socialData: {
      imageUrl: slug ? new URL(`/bolt-projects/${slug}`, SOCIAL_PREVIEW_ORIGIN).toString() : undefined,
      description,
    },
    generalData: {
      title: projectInfo?.title ?? DEFAULT_PROJECT_INFO.title,
      pageDescription: description,
    },
  });
};

export async function loader(args: LoaderFunctionArgs) {
  const { slug } = args.params;

  if (!slug) {
    throw json(null, { status: 404 });
  }

  const { env } = args.context.cloudflare;

  return handleWithAuth(
    args,
    async (_args, session) => {
      const [projectData, userData] = await Promise.all([
        loadProjectInfo({ slug }, session, true),
        withUserData({}, session, args.request, true),
      ]);

      const tokenStats = await getTokenStats(args, session, userData?.user);

      const projectInfo = projectData != null ? projectData.project : undefined;

      let github: IndexLoaderData['github'];

      if (projectData?.project.access.currentUserIsOwner) {
        const integration = await getAssociatedGithubRepository(
          { session, env },
          projectData.project.id,
          session.userId,
        );

        const url = new URL(args.request.url);

        const branchName = url.searchParams.get(GITHUB_BRANCH_PARAM);

        if (integration) {
          github = {
            linked: true,
            data: integration
              ? {
                  ...integration,
                  branchName: branchName ?? integration.branchName,
                }
              : undefined,
          };
        }
      } else if (projectData?.project.id) {
        const hasIntegration = await isLinkedToGithub(env, projectData.project.id);

        if (hasIntegration) {
          github = {
            linked: true,
          };
        }
      }

      return json({
        user: userData.user,
        github,
        slug,
        projectId: projectInfo?.id,
        projectInfo: projectInfo
          ? ({
              description: projectInfo.description,
              id: projectInfo.id,
              slug: projectInfo.slug,
              title: projectInfo.title,
            } satisfies ProjectInfo)
          : undefined,
        tokenStats,
        loggedIn: true,
      } satisfies IndexLoaderData);
    },
    async () => {
      const projectInfo = await loadProjectInfo({ slug });

      let github: boolean | undefined;

      if (projectInfo?.id) {
        github = await isLinkedToGithub(env, projectInfo.id);
      }

      return json({
        slug,
        projectId: projectInfo?.id,
        projectInfo,
        loggedIn: false,
        user: null,
        github: github ? { linked: true } : undefined,
      } satisfies IndexLoaderData);
    },
  );
}

export default function Index() {
  const data = useLoaderData<typeof loader>();

  const { slug } = data;
  const github = 'github' in data ? data.github : undefined;

  return (
    <IndexLayout>
      <ClientOnly>{() => <ProjectImport slug={slug} github={github} />}</ClientOnly>
    </IndexLayout>
  );
}
